import { withFingerprint } from "./credentialFingerprint";
import { Dispatch, SetStateAction, useCallback } from "react";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import {
  AuthInfoList,
  IAuthInfo,
  IDecryptedAuthInfo,
  IEncryptedAuthInfo,
} from "../../interfaces/AuthInfo";
import {
  DecryptAccountStatusEnum,
  decryptEncryptedAccountInfo,
} from "../../utils/encryption/accountPasswordEncryption";
import { encrypt } from "../../utils/encryption/encryption";
import { AuthInfoMap } from "./types";
import { getAccountKey, getAuthInfoStorageKey } from "./helpers";

interface IUseAuthActionsParams {
  decryptedAccountAuthInfo:
    | IDecryptedAuthInfo
    | IEncryptedAuthInfo
    | undefined;
  authInfoList: AuthInfoList;
  storeAuthInfoToLocalStorage: (info: AuthInfoList) => void;
  setAuthInfoList: Dispatch<SetStateAction<AuthInfoList>>;
  setLocalPassword: Dispatch<SetStateAction<string | undefined>>;
  setImportedAccount: Dispatch<SetStateAction<string | undefined>>;
  getImportedAccountAddress: () => string | null;
}

export function useAuthActions({
  decryptedAccountAuthInfo,
  authInfoList,
  storeAuthInfoToLocalStorage,
  setAuthInfoList,
  setLocalPassword,
  setImportedAccount,
  getImportedAccountAddress,
}: IUseAuthActionsParams) {
  // Set password and store to local storage
  const encryptAccount = useCallback(
    (password: string) => {
      if (!decryptedAccountAuthInfo) {
        return false;
      }

      const updatedAuthInfoList: AuthInfoList = { ...(authInfoList || {}) };

      // Make sure cannot encrypt encrypted account
      if (decryptedAccountAuthInfo.encrypted) {
        return false;
      }

      try {
        // Set password protected
        decryptedAccountAuthInfo.passwordProtected = true;
        const { encryptedText, salt, iv, iterations } = encrypt(
          JSON.stringify(decryptedAccountAuthInfo),
          password
        );

        const encryptedInfo: IEncryptedAuthInfo = {
          encrypted: true,
          passwordProtected: true,
          encryptedBundle: encryptedText,
          salt,
          iv,
          // Stored so this record stays decryptable if the default ever moves
          // again. Without it, changing the constant locks the user out.
          kdfIterations: iterations,
          account: decryptedAccountAuthInfo.account,
        };

        const accountKey = getAccountKey(decryptedAccountAuthInfo.account);
        if (!accountKey) {
          return false;
        }

        // Re-stamp: encryptedInfo is rebuilt from a fixed field list, which
        // drops the fingerprint carried by the decrypted source. Without this,
        // setting a password writes a record the read path treats as foreign
        // and immediately signs the user out.
        (updatedAuthInfoList as AuthInfoMap)[accountKey] = withFingerprint({
          ...encryptedInfo,
        });

        setAuthInfoList(updatedAuthInfoList);
        setLocalPassword(password);
        storeAuthInfoToLocalStorage(updatedAuthInfoList);
        return true;
      } catch (error) {
        return false;
      }
    },
    [decryptedAccountAuthInfo, authInfoList, storeAuthInfoToLocalStorage]
  );

  const unlockAccount = useCallback(
    (password: string) => {
      if (!decryptedAccountAuthInfo || !decryptedAccountAuthInfo.encrypted) {
        return false;
      }

      // Check password valid before storing password
      const { status } = decryptEncryptedAccountInfo(
        decryptedAccountAuthInfo,
        password
      );

      if (status === DecryptAccountStatusEnum.OK) {
        setLocalPassword(password);
        return true;
      }
      return false;
    },
    [decryptedAccountAuthInfo]
  );

  // TODO: - This is only for metamask
  const enableTrading = useCallback(
    (info: IAuthInfo, isRememberMeChecked: boolean) => {
      const accountKey = getAccountKey(info.account);
      if (!accountKey) {
        return;
      }

      const updatedAuthInfoList: AuthInfoMap = {
        ...((authInfoList || {}) as AuthInfoMap),
      };
      // Record which chain + backend minted this credential, so a build for a
      // different environment cannot silently reuse it. See credentialFingerprint.
      updatedAuthInfoList[accountKey] = withFingerprint(info);
      setAuthInfoList(updatedAuthInfoList as AuthInfoList);

      sessionStorage.setItem(
        getAuthInfoStorageKey(),
        JSON.stringify({ [accountKey]: updatedAuthInfoList[accountKey] })
      );

      if (isRememberMeChecked) {
        // Just save
        storeAuthInfoToLocalStorage(updatedAuthInfoList as AuthInfoList);
      }
    },
    [authInfoList, storeAuthInfoToLocalStorage]
  );

  const reconnectImportedAccount = useCallback(() => {
    const acc = getImportedAccountAddress();
    if (acc) {
      setImportedAccount(acc);
    }
  }, [getImportedAccountAddress]);

  const manualImportAccount = useCallback(
    (encryptedAuthInfo: IEncryptedAuthInfo, password: string) => {
      const accountKey = getAccountKey(encryptedAuthInfo.account);
      if (!accountKey) {
        return;
      }

      const updatedAuthInfoList: AuthInfoMap = {
        ...((authInfoList || {}) as AuthInfoMap),
      };
      // An imported credential is adopted by THIS build, so it is stamped for
      // this environment; otherwise the import appears to succeed and the app
      // reports REQUIRE_REGISTER_SIGNING as if nothing happened.
      updatedAuthInfoList[accountKey] = withFingerprint({
        ...encryptedAuthInfo,
      });

      setAuthInfoList(updatedAuthInfoList as AuthInfoList);
      setLocalPassword(password);
      storeAuthInfoToLocalStorage(updatedAuthInfoList as AuthInfoList);

      // Set imported account
      setImportedAccount(encryptedAuthInfo.account);
      window.localStorage.setItem(
        LocalStorageKeyEnum.IMPORTED_ACCOUNT_ADDRESS,
        encryptedAuthInfo.account
      );
    },
    [authInfoList, storeAuthInfoToLocalStorage]
  );

  const manualSyncAccount = useCallback(
    (decryptedAuthInfo: IDecryptedAuthInfo) => {
      const accountKey = getAccountKey(decryptedAuthInfo.account);
      if (!accountKey) {
        return;
      }

      const updatedAuthInfoList: AuthInfoMap = {
        ...((authInfoList || {}) as AuthInfoMap),
      };
      // Same as manualImportAccount — stamp on adoption.
      updatedAuthInfoList[accountKey] = withFingerprint({
        ...decryptedAuthInfo,
      });

      setAuthInfoList(updatedAuthInfoList as AuthInfoList);
      storeAuthInfoToLocalStorage(updatedAuthInfoList as AuthInfoList);

      // Set imported account
      setImportedAccount(decryptedAuthInfo.account);
      window.localStorage.setItem(
        LocalStorageKeyEnum.IMPORTED_ACCOUNT_ADDRESS,
        decryptedAuthInfo.account
      );
    },
    [authInfoList, storeAuthInfoToLocalStorage]
  );

  return {
    encryptAccount,
    unlockAccount,
    enableTrading,
    reconnectImportedAccount,
    manualImportAccount,
    manualSyncAccount,
  };
}
